import { createDocumentColumns, DocumentActionType } from "@/colDef/documentColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Spinner,
} from "@/components";
import DocumentDialog from "@/components/formDialog/DocumentDialog";
import {
  useDeleteDocumentMutation,
  useDocumentsQuery,
} from "@/hooks/api/document.queries";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import { DocumentType } from "@/services/document.service";
import { ShieldCheck } from "lucide-react";
import { useCallback, useMemo } from "react";

export default function Documents() {
  const { data: documents, isLoading, isError, refetch } = useDocumentsQuery();
  const deleteMutation = useDeleteDocumentMutation();

  const deleteDialog = useAlertDialog();
  const documentDialog = useFormDialog({
    mode: "create" as "create",
  });

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  const handleActionClick = useCallback(
    (payload: DocumentActionType) => {
      if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Are you sure you want to completely remove this document? If this document was already verified by your college, deleting it might drop your verification status.
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.id,
          action: "delete",
        });
      }
    },
    [deleteDialog]
  );

  const columns = useMemo(
    () => createDocumentColumns(handleActionClick),
    [handleActionClick]
  );

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load documents."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageContainer className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Secure Documents</h1>
        <Button
          onClick={() => documentDialog.open({})}
          className="flex items-center gap-2 bg-slate-800 text-white hover:bg-slate-900 shadow-md"
        >
          <ShieldCheck size={16} />
          Upload New Document
        </Button>
      </div>

      <Card className="shadow-lg border-t-4 border-t-slate-800">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<DocumentType, any>
              columns={columns}
              data={documents || []}
            />
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <DocumentDialog
        isOpen={documentDialog.state.isOpen}
        onOpenChange={(open) => !open && documentDialog.close()}
        onCancel={documentDialog.close}
      />

      {/* Delete Confirmation */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Delete Document"
        description={deleteDialog.state.message}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
}
